import {
  AlertTriangle,
  Building2,
  CalendarRange,
  Check,
  CheckSquare,
  ClipboardCheck,
  CreditCard,
  FileQuestion,
  Layers,
  Library,
  Settings,
  Users,
} from 'lucide-react'
import { Link } from '#router'
import { formatNeedsAction } from '@/lib/audition/queue'
import { trpc } from '@/lib/trpc'
import { RecipesSection } from './shared/recipes-section'
import { StudyPlansSection } from './shared/study-plans-section'
import { SystemDesignStudyPlansSection } from './shared/system-design-study-plans-section'
import { TaxonomySection } from './shared/taxonomy-section'
import { TestTemplatesSection } from './shared/test-templates-section'

interface NavCardProps {
  href: string
  icon: React.ReactNode
  title: string
  description: string
  gradient: string
  /** Optional line under the description — see AuditionsNavCard. */
  badge?: React.ReactNode
}

function NavCard({
  href,
  icon,
  title,
  description,
  gradient,
  badge,
}: NavCardProps) {
  return (
    <Link
      href={href}
      className="bg-card border-border hover:border-primary/50 group flex items-center gap-4 rounded-lg border p-4 transition-all hover:shadow-md"
    >
      <div className={`rounded-lg p-3 ${gradient}`}>{icon}</div>
      <div className="flex-1">
        <h3 className="text-foreground font-semibold group-hover:text-primary transition-colors">
          {title}
        </h3>
        <p className="text-muted-foreground text-sm">{description}</p>
        {badge}
      </div>
    </Link>
  )
}

/**
 * The applications card, with the count of what is owed (LG-414).
 *
 * This badge is the whole prompting mechanism: before it, an applicant could
 * sit at "Applied" indefinitely and nothing anywhere said so. It has to be
 * visible without navigating, which is why it lives on the card rather than
 * on the queue it links to.
 *
 * While the count is loading the card renders without it rather than showing
 * a zero — "nothing needs your action" is a claim, and guessing it is worse
 * than saying nothing.
 *
 * A count that FAILED says so, though. Silence is right for the second the
 * query is in flight and wrong for a query that will never answer: this
 * badge is the only thing anywhere that tells an admin work is owed, so a
 * card that quietly stops carrying it looks exactly like a cohort with
 * nothing waiting — which is the failure LG-414 exists to end.
 */
function AuditionsNavCard() {
  const summary = trpc.admin.auditions.queueSummary.useQuery()
  const data = summary.data

  return (
    <NavCard
      href="/admin/auditions"
      icon={<ClipboardCheck size={24} className="text-white" />}
      title="Applications"
      description="Review the queue and decide who joins the cohort"
      gradient="bg-gradient-to-br from-fuchsia-500 to-purple-600"
      badge={
        data ? (
          data.needsAction > 0 ? (
            <p className="text-destructive mt-1 flex items-center gap-1 text-sm font-medium">
              <AlertTriangle aria-hidden="true" className="size-3.5" />
              {formatNeedsAction(data.needsAction)}
              {data.overdue > 0 ? ` · ${data.overdue} overdue` : ''}
            </p>
          ) : (
            <p className="text-muted-foreground mt-1 flex items-center gap-1 text-sm">
              <Check aria-hidden="true" className="size-3.5" />
              Nothing needs your action
            </p>
          )
        ) : summary.isError ? (
          <p className="text-muted-foreground mt-1 flex items-center gap-1 text-sm">
            <AlertTriangle aria-hidden="true" className="size-3.5" />
            Couldn't load the count — open the queue
          </p>
        ) : null
      }
    />
  )
}

export function AdminDashboard() {
  return (
    <>
      {/* Header */}
      <header className="bg-background/95 border-border sticky top-0 z-10 border-b backdrop-blur-sm">
        <div className="mx-auto max-w-6xl px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 p-2">
              <Settings size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-foreground text-xl font-bold">
                Admin Dashboard
              </h1>
              <p className="text-muted-foreground text-sm">
                Manage clients, problems, and content
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-6xl px-6 py-6">
        {/* Navigation Cards */}
        <div className="mb-8">
          <h2 className="text-foreground mb-4 text-lg font-semibold">
            Quick Access
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <NavCard
              href="/admin/coaching"
              icon={<Users size={24} className="text-white" />}
              title="Users"
              description="Manage clients, or promote platform users"
              gradient="bg-gradient-to-br from-sky-500 to-indigo-600"
            />
            <AuditionsNavCard />
            <NavCard
              href="/admin/problems"
              icon={<FileQuestion size={24} className="text-white" />}
              title="Problems"
              description="Edit and manage coding problems"
              gradient="bg-gradient-to-br from-orange-500 to-red-600"
            />
            <NavCard
              href="/admin/companies"
              icon={<Building2 size={24} className="text-white" />}
              title="Companies"
              description="View company problem data"
              gradient="bg-gradient-to-br from-emerald-500 to-teal-600"
            />
            <NavCard
              href="/admin/sources"
              icon={<Library size={24} className="text-white" />}
              title="Sources"
              description="Manage books, courses, and content"
              gradient="bg-gradient-to-br from-amber-500 to-orange-600"
            />
            <NavCard
              href="/admin/system-design"
              icon={<Layers size={24} className="text-white" />}
              title="System Design"
              description="Manage system design problems"
              gradient="bg-gradient-to-br from-blue-500 to-indigo-600"
            />
            <NavCard
              href="/admin/tasks"
              icon={<CheckSquare size={24} className="text-white" />}
              title="Tasks"
              description="Manage reusable homework tasks"
              gradient="bg-gradient-to-br from-violet-500 to-purple-600"
            />
            <NavCard
              href="/admin/sprints"
              icon={<CalendarRange size={24} className="text-white" />}
              title="Sprints"
              description="The weekly calendar; add clients late, grant credits, mark reduced weeks"
              gradient="bg-gradient-to-br from-cyan-500 to-sky-600"
            />
            <NavCard
              href="/admin/catalog"
              icon={<CreditCard size={24} className="text-white" />}
              title="Catalog"
              description="Stripe packs & metadata health"
              gradient="bg-gradient-to-br from-teal-500 to-emerald-600"
            />
          </div>
        </div>

        {/* Study Plans Section */}
        <StudyPlansSection />

        {/* System Design Study Plans Section */}
        <SystemDesignStudyPlansSection />

        {/* Taxonomy Section */}
        <TaxonomySection />

        {/* Test Templates Section */}
        <TestTemplatesSection />

        {/* Recipes Section */}
        <RecipesSection />
      </main>
    </>
  )
}
